import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { getUserRole } from '../util';
import { CustomTableFoot, CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { Columns } from './TrialDetailColumns';
import { useNavigate } from 'react-router-dom';
import RightArrow from '../../components/icons/RightArrow';
import moment from 'moment';
import { useGetFinanceTrialsQuery } from '../../store/slices/apiSlice';
import CustomTablePagination from '../../components/common/TablePagination';
import { updatePagination } from '../../store/slices/applicationSlice';

const FinanceTrialTable = () => {
  const user = useSelector(selectCurrentUser);
  const userData = useSelector((state) => state.auth.userData);
  const userRole = getUserRole(user, null, null, userData);
  const sponsorDetails = useSelector((state) => state.userDetails.user);
  const theme = useTheme();
  const columns = Columns();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { page, rowsPerPage } = useSelector((state) => state.application.pagination.FINANCE_TRIALS_TABLE);
  const isSponsor = userRole === 'sponsor';
  const { data } = useGetFinanceTrialsQuery(
    {
      sponsorEntityId: sponsorDetails?.financeDetail?.entityId,
      skipStatus: !isSponsor ? 'Drafted' : 'Archived',
      page: page + 1,
      limit: rowsPerPage,
    },
    {
      skip: !sponsorDetails?.financeDetail?.entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const onNewPage = (page, rowsPerPage) => {
    dispatch(updatePagination({ tableType: 'FINANCE_TRIALS_TABLE', page, rowsPerPage }));
  };

  const handleNavigate = (row) => {
    row.status == 'Drafted' || row.status == 'PendingApproval'
      ? navigate(`/createFinanceTrial/${row.id}`)
      : navigate(`/financeTrial/${row.id}`);
  };
  return (
    <>
      <Box sx={{ py: 2 }}>
        <Typography variant="h6" color="initial">
          Finance Trials
        </Typography>
      </Box>
      <Paper sx={{ width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            height: 3,
            backgroundColor: theme.palette.primary.light,
          }}
        />
        <TableContainer sx={{ maxHeight: 500, maxWidth: '100vW' }}>
          <Table stickyHeader aria-label="sticky table">
            <CustomTableHead>
              <TableRow>
                {columns.map((column) => {
                  if (['totalBudget'].includes(column.id)) return;
                  return (
                    <CustomTableHeadCell key={column.id} align={column.align} sx={{ ...column?.style }}>
                      {column.label}
                    </CustomTableHeadCell>
                  );
                })}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {data && data?.data?.length > 0 ? (
                data?.data.map((row) => {
                  return (
                    <CustomTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      onClick={() => handleNavigate(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {columns.map((column) => {
                        if (['totalBudget'].includes(column.id)) return;
                        let value = null;
                        const style = { ...column?.style };
                        switch (column.id) {
                          case 'trialTitle':
                            value = row.title;
                            break;
                          case 'siteCount':
                            value = row?.clinicalSites?.length;
                            break;
                          case 'status': {
                            style.fontWeight = 600;
                            if (row.status === 'Created' || row.status === 'Ongoing') {
                              style.color = 'green';
                            } else {
                              style.color = 'orange';
                            }
                            value = row.status;
                            break;
                          }
                          default:
                            value = row[column.id];
                            break;
                        }

                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Typography variant="subtitle2" color="initial" style={style}>
                              {value}
                            </Typography>
                            {column.id === 'action' && <RightArrow onClick={() => handleNavigate(row)} />}
                          </TableCell>
                        );
                      })}
                    </CustomTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {userRole === 'PI' || userRole === 'site_coordinator' ? (
                      <Typography variant="subtitle1" color="initial">
                        No Created Trials Found
                      </Typography>
                    ) : (
                      <Typography variant="subtitle1" color="initial">
                        No Draft Trials Found
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <CustomTableFoot>
              <CustomTableRow sx={{ right: 0 }}>
                <CustomTableHeadCell colSpan={columns.length}>
                  <CustomTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    count={data?.pagination?.total}
                    onNewPage={onNewPage}
                  />
                </CustomTableHeadCell>
              </CustomTableRow>
            </CustomTableFoot>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default FinanceTrialTable;
